import React from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { TextField, Box, CircularProgress, Autocomplete } from "@mui/material";
import FormDialog from "@components/FormDialog";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "@context/SnackBarProvider";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import useHandleError from "@lib/utils/errorHook";
import { UnitGroupResponse } from "@lib/types/unit_group";
import { getUnits } from "@lib/api/queries/units";
import {
  createUnitGroup,
  UnitGroupFormData,
  unitGroupSchema,
} from "@lib/api/mutations/unit_groups";
import { UnitList } from "@lib/types/units";

interface Props {
  addUnitGroupDialogOpen: boolean;
  setAddUnitGroupDialogOpen: (open: boolean) => void;
  refetch?: () => void;
}

const CreateUnitGroup: React.FC<Props> = ({
  addUnitGroupDialogOpen,
  setAddUnitGroupDialogOpen,
  refetch,
}) => {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const handleError = useHandleError();

  const {
    data: units,
    isLoading,
    isFetching,
    refetch: refetchUnits,
    error,
  } = useQuery<UnitList>({
    queryKey: ["unit"],
    queryFn: () => getUnits(),
    refetchOnWindowFocus: false,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<UnitGroupFormData>({
    resolver: zodResolver(unitGroupSchema),
    defaultValues: {
      name: "",
      units: [],
    },
  });

  const { mutate, isPending: isCreatingUnitGroup } = useMutation({
    mutationFn: createUnitGroup,
    onSuccess: (data: UnitGroupResponse) => {
      showSnackbar(t("user.driverGroups.addSuccess"), "success");

      setAddUnitGroupDialogOpen(false);
      reset();
      refetch && refetch();
    },
    onError: (error: AxiosError | unknown) => {
      if (error instanceof AxiosError && error.response) {
        const errorDetails = handleError(error.response.data);
        errorDetails && showSnackbar(errorDetails.message, "error");
      } else {
        showSnackbar(t("general.error"), "error");
      }
    },
  });

  const handleAddDriver = (data: UnitGroupFormData) => {
    mutate(data);
  };

  return (
    <FormDialog
      title={t("user.drivers.add")}
      open={addUnitGroupDialogOpen}
      onClose={() => {
        setAddUnitGroupDialogOpen(false);
        reset();
      }}
      onSubmit={handleSubmit(handleAddDriver)}
      submitText={t("general.save")}
      isSubmitting={isCreatingUnitGroup}
      submittingText={t("general.saving")}
    >
      {isFetching && <CircularProgress />}
      {!isFetching && (
        <form noValidate>
          <Box display="flex" flexDirection="column" gap={2} py={2}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("general.name")}
                  variant="outlined"
                  fullWidth
                  error={!!errors.name}
                  helperText={errors.name?.message}
                />
              )}
            />

            <Controller
              name="units"
              control={control}
              render={({ field }) => (
                <div>
                  <Autocomplete
                    {...field}
                    multiple
                    options={units?.units || []}
                    value={
                      units?.units?.filter((unit) =>
                        field.value?.includes(unit.unit_id)
                      ) || []
                    }
                    onChange={(_, value) => {
                      field.onChange(
                        value ? value.map((unit) => unit.unit_id) : []
                      );
                    }}
                    loading={isFetching}
                    isOptionEqualToValue={(option, value) =>
                      option.unit_id === value.unit_id
                    }
                    getOptionLabel={(option) => option.name || ""}
                    renderOption={(props, option) => (
                      <li {...props} key={`${option.name}-${option.unit_id}`}>
                        {option.name}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("user.unitGroups.units")}
                        placeholder={t("user.unitGroups.selectUnits")}
                        variant="outlined"
                        error={!!errors.units}
                        helperText={errors.units?.message}
                      />
                    )}
                  />
                </div>
              )}
            />
          </Box>
        </form>
      )}
    </FormDialog>
  );
};

export default CreateUnitGroup;
